import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

const AzulCheckoutScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mesaId, subtotal, tipPercentage, tipAmount, waiterName, staffId } = location.state || {};

  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Format card number with spaces (4 digits each)
  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const chunks = cleaned.match(/.{1,4}/g) || [];
    return chunks.join(' ').substr(0, 19); // Max 16 digits + 3 spaces
  };

  // Format expiry as MM/YY
  const formatExpiry = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.substr(0, 2) + '/' + cleaned.substr(2, 2);
    }
    return cleaned;
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    setCardNumber(formatted);
    if (errors.cardNumber) setErrors({ ...errors, cardNumber: '' });
  };

  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatExpiry(e.target.value);
    setExpiry(formatted);
    if (errors.expiry) setErrors({ ...errors, expiry: '' });
  };

  const handleCvcChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').substr(0, 4);
    setCvc(value);
    if (errors.cvc) setErrors({ ...errors, cvc: '' });
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    const cardDigits = cardNumber.replace(/\s/g, '');
    if (cardDigits.length !== 16) {
      newErrors.cardNumber = 'Número de tarjeta inválido';
    }

    if (expiry.length !== 5) {
      newErrors.expiry = 'Fecha inválida';
    } else {
      const [month, year] = expiry.split('/');
      const monthNum = parseInt(month);
      if (monthNum < 1 || monthNum > 12) {
        newErrors.expiry = 'Mes inválido';
      }
    }

    if (cvc.length < 3) {
      newErrors.cvc = 'CVC inválido';
    }

    if (!cardholderName.trim()) {
      newErrors.cardholderName = 'Nombre requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    if (!validateForm()) return;

    // Convert expiry MM/YY to YYYYMM format for Azul
    const [month, year] = expiry.split('/');
    const fullYear = '20' + year; // Assumes 20xx
    const azulExpiry = fullYear + month; // YYYYMM

    navigate('/payment-processing', {
      state: {
        mesaId,
        subtotal,
        tipPercentage,
        tipAmount,
        // Card data
        cardNumber: cardNumber.replace(/\s/g, ''),
        cardExpiration: azulExpiry,
        cardCVC: cvc,
        cardholderName
      }
    });
  };

  return (
    <div className="min-h-screen bg-white w-full overflow-x-hidden">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center">
        <button 
          onClick={() => navigate(-1)}
          className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100"
        >
          <ChevronLeft className="w-6 h-6 text-gray-600" />
        </button>
        <h1 className="flex-1 text-center text-lg font-semibold text-gray-900 pr-10">
          Información de tarjeta
        </h1>
      </div>

      {/* Content */}
      <div className="max-w-md mx-auto px-4 sm:px-6 py-6 pb-24">
        

        {/* Card Number */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Número de tarjeta
          </label>
          <input
            type="tel"
            inputMode="numeric"
            autoComplete="cc-number"
            value={cardNumber}
            onChange={handleCardNumberChange}
            placeholder="1234 5678 9012 3456"
            className={`w-full px-4 py-3 border rounded-lg text-base min-h-[44px] ${
              errors.cardNumber ? 'border-red-500' : 'border-gray-300'
            } focus:outline-none focus:ring-2 focus:ring-black`}
            maxLength={19}
          />
          {errors.cardNumber && (
            <p className="text-red-500 text-xs mt-1">{errors.cardNumber}</p>
          )}
        </div>

        {/* Expiry and CVC */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vencimiento
            </label>
            <input
              type="tel"
              inputMode="numeric"
              autoComplete="cc-exp"
              value={expiry}
              onChange={handleExpiryChange}
              placeholder="MM/AA"
              className={`w-full px-4 py-3 border rounded-lg text-base min-h-[44px] ${
                errors.expiry ? 'border-red-500' : 'border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-black`}
              maxLength={5}
            />
            {errors.expiry && (
              <p className="text-red-500 text-xs mt-1">{errors.expiry}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CVC
            </label>
            <input
              type="tel"
              inputMode="numeric"
              autoComplete="cc-csc"
              value={cvc}
              onChange={handleCvcChange}
              placeholder="123"
              className={`w-full px-4 py-3 border rounded-lg text-base min-h-[44px] ${
                errors.cvc ? 'border-red-500' : 'border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-black`}
              maxLength={4}
            />
            {errors.cvc && (
              <p className="text-red-500 text-xs mt-1">{errors.cvc}</p>
            )}
          </div>
        </div>

        {/* Cardholder Name */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nombre en la tarjeta
          </label>
          <input
            type="text"
            autoComplete="cc-name"
            value={cardholderName}
            onChange={(e) => {
              setCardholderName(e.target.value);
              if (errors.cardholderName) setErrors({ ...errors, cardholderName: '' });
            }}
            placeholder="JUAN PEREZ"
            className={`w-full px-4 py-3 border rounded-lg text-base min-h-[44px] uppercase ${
              errors.cardholderName ? 'border-red-500' : 'border-gray-300'
            } focus:outline-none focus:ring-2 focus:ring-black`}
          />
          {errors.cardholderName && (
            <p className="text-red-500 text-xs mt-1">{errors.cardholderName}</p>
          )}
        </div>

        {/* Security Notice */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-gray-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-gray-900 mb-1">Pago seguro</p>
              <p className="text-xs text-gray-600">
                Tu información está protegida con encriptación de extremo a extremo.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Bottom Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 safe-bottom">
        <div className="max-w-md mx-auto px-4 sm:px-6 py-4">
          <button
            onClick={handleContinue}
            className="w-full bg-black text-white py-4 rounded-full text-lg font-bold hover:bg-gray-800 transition-colors"
          >
            Continuar al pago
          </button>
        </div>
      </div>
    </div>
  );
};

export default AzulCheckoutScreen;
